import React, { useMemo, useState } from "react";
import { activeTab, useConsole } from "../lib/console";
import { useNow } from "../lib/hooks";
import { SEVERITIES, attackLabel, fmtTime, plural, sevLabel, sevTone, sevVar, timeAgo, toDate } from "../lib/format";
import { Badge, Button, Callout, Card, Chip, CountryTag, Empty, SearchInput, Segmented, SevBadge, cx } from "../components/ui";
import { Icon } from "../components/Icon";
import { PageHeader, PageTabs } from "../components/ConsoleShell";

const PAGE = 60;

function LiveFeed() {
  const c = useConsole();
  const now = useNow(15000);
  const [q, setQ] = useState("");
  const [sev, setSev] = useState("ALL");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("all");
  const [frozen, setFrozen] = useState(null);
  const [limit, setLimit] = useState(PAGE);

  const live = useMemo(() => c.threats.filter((t) => t.is_threat !== false), [c.threats]);
  const source = frozen || live;
  const newSince = frozen ? Math.max(0, live.length - frozen.length) : 0;

  const types = useMemo(() => {
    const m = new Map();
    source.forEach((t) => m.set(t.attack_type, (m.get(t.attack_type) || 0) + 1));
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [source]);

  const sevCounts = useMemo(() => {
    const m = {};
    source.forEach((t) => (m[t.severity] = (m[t.severity] || 0) + 1));
    return m;
  }, [source]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return source.filter((t) => {
      if (sev !== "ALL" && t.severity !== sev) return false;
      if (type && t.attack_type !== type) return false;
      const blocked = t.contained || c.blockedIPs.has(t.source_ip);
      if (status === "open" && blocked) return false;
      if (status === "contained" && !blocked) return false;
      if (!needle) return true;
      return [t.source_ip, t.destination_ip, t.country, t.attack_type, attackLabel(t.attack_type), t.username, t.mitre_technique_id, t.city]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle));
    });
  }, [source, q, sev, type, status, c.blockedIPs]);

  const filtered = q || sev !== "ALL" || type || status !== "all";
  const clear = () => {
    setQ("");
    setSev("ALL");
    setType("");
    setStatus("all");
  };

  return (
    <Card flush>
      <div className="table-toolbar">
        <SearchInput value={q} onChange={setQ} placeholder="Search IP, country, technique, account…" label="Search detections" />
        <select className="select" style={{ width: "auto", height: 34 }} value={type} onChange={(e) => setType(e.target.value)} aria-label="Attack type">
          <option value="">All attack types</option>
          {types.map(([k, n]) => (
            <option key={k} value={k}>
              {attackLabel(k)} ({n})
            </option>
          ))}
        </select>
        <Segmented
          size="sm"
          label="Response status"
          value={status}
          onChange={setStatus}
          options={[
            { id: "all", label: "All" },
            { id: "open", label: "Not blocked" },
            { id: "contained", label: "Blocked" },
          ]}
        />
        <div className="tb-spacer" />
        <Button
          size="sm"
          variant={frozen ? "primary" : "secondary"}
          icon={frozen ? "play" : "pause"}
          onClick={() => setFrozen(frozen ? null : live)}
          title={frozen ? "Resume live updates" : "Freeze the list to read it"}
        >
          {frozen ? (newSince ? `Resume · ${newSince} new` : "Resume") : "Pause feed"}
        </Button>
      </div>
      <div className="table-toolbar" style={{ paddingTop: 10, paddingBottom: 10 }}>
        <div className="row row-wrap" role="group" aria-label="Severity filter">
          <Chip active={sev === "ALL"} onClick={() => setSev("ALL")} count={source.length}>
            All severities
          </Chip>
          {SEVERITIES.slice(0, 4).map((s) => (
            <Chip key={s} tone={sevTone(s)} active={sev === s} onClick={() => setSev(sev === s ? "ALL" : s)} count={sevCounts[s] || 0}>
              <i style={{ width: 7, height: 7, borderRadius: "50%", background: sevVar(s) }} />
              {sevLabel(s)}
            </Chip>
          ))}
        </div>
        <div className="tb-spacer" />
        <span className="small muted" aria-live="polite">
          {frozen && (
            <Badge tone="brand" dot>
              Paused
            </Badge>
          )}{" "}
          Showing {Math.min(rows.length, limit)} of {plural(rows.length, "detection")}
        </span>
      </div>

      {rows.length === 0 ? (
        filtered ? (
          <Empty icon="filter" title="No detections match" action={<Button size="sm" onClick={clear}>Clear filters</Button>}>
            Try a broader search or another severity.
          </Empty>
        ) : (
          <Empty icon="radar" title="Your feed starts here">
            Detections stream in as the pipeline analyses events. Check that simulated traffic is running or send events to the ingest API.
          </Empty>
        )
      ) : (
        <div className="table-wrap table-scroll" style={{ maxHeight: "calc(100vh - 330px)", minHeight: 360 }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Severity</th>
                <th>Detection</th>
                <th>Source</th>
                <th className="table-hide-sm">Target</th>
                <th className="table-hide-sm">Response</th>
                <th className="num">Time (GST)</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, limit).map((t, i) => {
                const blocked = t.contained || c.blockedIPs.has(t.source_ip);
                return (
                  <tr
                    key={`${t.source_ip}-${t.timestamp}-${i}`}
                    className={cx("is-clickable", !frozen && t._new && now - t._new < 4000 && "is-new")}
                    tabIndex={0}
                    onClick={() => c.openDetection(t)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        c.openDetection(t);
                      }
                    }}
                    aria-label={`${sevLabel(t.severity)} ${attackLabel(t.attack_type)} from ${t.source_ip}`}
                  >
                    <td>
                      <SevBadge sev={t.severity} />
                    </td>
                    <td>
                      <div className="row" style={{ gap: 8 }}>
                        <span className="strong" style={{ fontWeight: 500 }}>
                          {attackLabel(t.attack_type)}
                        </span>
                        {t.username && <Badge tone="high">@{t.username}</Badge>}
                      </div>
                      <div className="tiny muted mono" style={{ marginTop: 2, marginLeft: 16 }}>
                        {t.mitre_technique_id || "—"} {t.mitre_tactic && t.mitre_tactic !== "N/A" ? `· ${t.mitre_tactic}` : ""}
                      </div>
                    </td>
                    <td>
                      <div className="mono" style={{ fontSize: 12.5 }}>
                        {t.source_ip}
                      </div>
                      <div style={{ marginTop: 3 }}>
                        <CountryTag code={t.country_code} name={t.country} />
                      </div>
                    </td>
                    <td className="table-hide-sm mono muted" style={{ fontSize: 12.5 }}>
                      {t.destination_ip}
                      {t.destination_port ? `:${t.destination_port}` : ""}
                    </td>
                    <td className="table-hide-sm">
                      {blocked ? (
                        <Badge tone="ok" dot>
                          Blocked
                        </Badge>
                      ) : (
                        <Badge tone="neutral">Monitoring</Badge>
                      )}
                    </td>
                    <td className="num">
                      <div className="mono" style={{ fontSize: 12.5 }}>
                        {fmtTime(t.timestamp)}
                      </div>
                      <div className="tiny muted">{timeAgo(t.timestamp, now)}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length > limit && (
            <div style={{ padding: 14, display: "flex", justifyContent: "center", borderTop: "1px solid var(--line)" }}>
              <Button size="sm" variant="ghost" onClick={() => setLimit((l) => l + PAGE)}>
                Show {Math.min(PAGE, rows.length - limit)} more
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function Alerts() {
  const c = useConsole();
  const now = useNow(15000);
  const [view, setView] = useState("open");

  const groups = useMemo(() => {
    const cutoff = now - 30 * 60 * 1000;
    const open = [];
    const handled = [];
    c.alerts.forEach((a) => {
      if (a.acknowledged) handled.push(a);
      else if ((toDate(a.timestamp)?.getTime() ?? 0) >= cutoff) open.push(a);
    });
    return { open, handled, all: c.alerts };
  }, [c.alerts, now]);

  const list = groups[view];

  return (
    <div className="stack">
      <Callout tone="info" icon="info">
        Alerts from a source the response policy has already blocked are closed automatically as <strong style={{ display: "inline" }}>contained</strong>. Open alerts
        from the last 30 minutes are the ones that still need an analyst.
      </Callout>
      <Card
        flush
        title="Alerts"
        subtitle="Raised by the alert manager from high-signal detections"
        icon="bell"
        actions={
          <Segmented
            size="sm"
            label="Alert status"
            value={view}
            onChange={setView}
            options={[
              { id: "open", label: "Needs review", count: groups.open.length },
              { id: "handled", label: "Contained", count: groups.handled.length },
              { id: "all", label: "All", count: groups.all.length },
            ]}
          />
        }
      >
        {list.length === 0 ? (
          view === "open" ? (
            <Empty icon="checkCircle" tone="ok" title="No alerts need review">
              Everything recent was contained automatically or is below alerting severity.
            </Empty>
          ) : (
            <Empty icon="bell" title="No alerts here yet" />
          )
        ) : (
          <div className="list">
            {list.slice(0, 120).map((a, i) => (
              <button key={`${a.id}-${i}`} type="button" className="list-item" onClick={() => c.openProfile(a.source_ip)} style={{ alignItems: "flex-start" }}>
                <span className={cx("sev-glyph", `tone-${sevTone(a.severity)}`)}>
                  <Icon name={a.severity === "CRITICAL" ? "shieldAlert" : "alert"} size={16} />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span className="list-title" style={{ display: "block" }}>
                    {a.title || attackLabel(a.attack_type)}
                  </span>
                  <span className="small dim" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", marginTop: 2 }}>
                    {a.message}
                  </span>
                  <span className="list-meta" style={{ marginTop: 6 }}>
                    <span className="mono">{a.source_ip}</span>
                    <CountryTag code={a.country_code} name={a.country} />
                    {a.mitre_technique_id && <span className="mono">{a.mitre_technique_id}</span>}
                  </span>
                </span>
                <span className="list-side">
                  <SevBadge sev={a.severity} />
                  {a.acknowledged ? <Badge tone="ok">Contained</Badge> : <Badge tone="medium">Open</Badge>}
                  <span>{timeAgo(a.timestamp, now)}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export default function Detections() {
  const c = useConsole();
  const tab = activeTab("detections", c.tab);
  const total = c.threats.filter((t) => t.is_threat !== false).length;
  return (
    <>
      <PageHeader
        eyebrow="Monitor"
        icon="radar"
        title="Detections"
        description="Review incoming threats and triage alerts from your environment."
        actions={
          <Button icon="globe" onClick={c.openWarRoom}>
            War-room map
          </Button>
        }
      />
      <PageTabs page="detections" counts={{ feed: total, alerts: c.level.open }} />
      {tab === "alerts" ? <Alerts /> : <LiveFeed />}
    </>
  );
}
